import React, { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { T, IMAGES } from "@/content/site";
import { scrollToId } from "@/hooks/useLenis";

export default function Hero() {
  const { t } = useLang();
  const lines = t(T.hero.lines);

  // subtle 3D parallax on hero image
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { damping: 20, stiffness: 80 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), { damping: 20, stiffness: 80 });
  const tx = useSpring(useTransform(mx, [-0.5, 0.5], [-20, 20]), { damping: 25, stiffness: 90 });
  const ty = useSpring(useTransform(my, [-0.5, 0.5], [-20, 20]), { damping: 25, stiffness: 90 });

  useEffect(() => {
    const handler = (e) => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      mx.set(x);
      my.set(y);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, [mx, my]);

  return (
    <section id="hero" data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden bg-black">
      {/* Background image with slow zoom + subtle parallax */}
      <motion.div
        className="absolute inset-0"
        style={{ perspective: 1200 }}
      >
        <motion.div
          className="absolute inset-[-4%]"
          style={{ rotateX: rx, rotateY: ry, x: tx, y: ty }}
        >
          <motion.img
            src={IMAGES.hero}
            alt="Agave field at dusk"
            loading="eager"
            className="w-full h-full object-cover"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1.05 }}
            transition={{ duration: 6, ease: [0.19, 1, 0.22, 1] }}
          />
        </motion.div>
      </motion.div>

      {/* Vignette + gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/85 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_75%)] pointer-events-none" />

      {/* Top overline */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.9, ease: "easeOut" }}
        className="absolute top-[110px] left-6 md:left-10 z-10 overline"
        data-testid="hero-overline"
      >
        <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
        {t(T.hero.overline)}
      </motion.div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-end pb-[100px] md:pb-[120px]">
        <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h1 data-testid="hero-headline" className="font-serif text-white leading-[0.95] tracking-[-0.01em] text-[44px] sm:text-[60px] md:text-[88px] lg:text-[112px]">
                {lines.map((line, i) => (
                  <span key={i} className="mask-line">
                    <motion.span
                      initial={{ y: "110%", opacity: 0 }}
                      animate={{ y: "0%", opacity: 1 }}
                      transition={{
                        duration: 1.3,
                        delay: 0.35 + i * 0.18,
                        ease: [0.19, 1, 0.22, 1],
                      }}
                      className={i === 2 ? "italic text-[color:var(--sacrvm-gold)]" : ""}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>
            </div>

            <div className="col-span-12 lg:col-span-4 lg:pl-6">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 1, ease: "easeOut" }}
                className="text-white/75 text-[15px] leading-[1.7] max-w-[440px] mb-8"
                data-testid="hero-body"
              >
                {t(T.hero.body)}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8, duration: 0.9, ease: "easeOut" }}
                className="flex flex-wrap gap-3"
              >
                <button data-testid="hero-cta-collection" onClick={() => scrollToId("collection")} className="btn-sharp gold">
                  {t(T.hero.ctaCollection)}
                  <ArrowRight size={14} className="arrow" />
                </button>
                <button data-testid="hero-cta-brand" onClick={() => scrollToId("private-label")} className="btn-sharp">
                  {t(T.hero.ctaBrand)}
                  <ArrowRight size={14} className="arrow" />
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-white/60"
        data-testid="hero-scroll"
      >
        <span className="overline !text-white/50">{t(T.hero.scroll)}</span>
        <motion.span
          className="block w-px h-16 bg-white/40 origin-top"
          animate={{ scaleY: [0.2, 1, 0.2] }}
          transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
        />
      </motion.div>

      {/* corner meta */}
      <div className="hidden md:block absolute top-[110px] right-10 z-10 text-right">
        <div className="overline !text-white/50">EST. MMXIII</div>
        <div className="font-serif text-white text-[18px] tracking-[0.14em] mt-1">Oaxaca · México</div>
      </div>
    </section>
  );
}
